import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as remove from 'lodash/remove';

import { CellData } from '../models/cell-data';
import { ComponentData } from '../models/component-data';
import { TabConfig } from '../models/tab-config';
import { TabData } from '../models/tab-data';
import { TableData } from '../models/table-data';

export class TabUtil {
	/**
	 * isEmptyTable is used to check if table is empty
	 * (no rawValue & no formattedValue)
	 *
	 * @param {table} tableData
	 * @returns {boolean}
	 */
	public static isEmptyTable(table: TableData): boolean {
		const cells = table.cells ? table.cells : [];
		for (const row of cells) {
			for (const col of row) {
				if (col && (col.rawValue || col.formattedValue)) {
					return false;
				}
			}
		}
		return true;
	}

	/**
	 * hasTabWithNoError is used to check if there is at least one tab doesn't have error in a component
	 *
	 * @param {TabData[]} tabs
	 * @returns {boolean}
	 */
	public static hasTabWithNoError(tabs: TabData[]): boolean {
		return tabs.length === 0 || tabs.some(tab => !tab.error);
	}

	public static isEmptyTab(tabData: TabData): boolean {
		if (!tabData) { return true; }

		const tableData: TableData[] = tabData.tableData;
		for (const data of tableData) {
			for (const row of data.cells) {
				for (const col of row) {
					if (col.rawValue && col['config']['id'] !== 'asOfDate') {
						return false;
					}
				}
			}
		}
		return true;
	}

	public static isTabValid(tabData: TabData): boolean {
		return !isEmpty(tabData) && !tabData.error && tabData.displayConfig?.visible;
	}

	private static getVisibleData(...dataArray: CellData[][]): void {
		dataArray.forEach(data => remove(data, item => !item.config.visible));
	}

	public static getVisibleTabData(tabData: TabData, ignoreEmptyRow: boolean = false): TabData {
		if (tabData) {
			const tableData = get(tabData, 'tableData[0]');
			this.getVisibleData(get(tableData, 'headersX'), get(tableData, 'headersY'));
			get(tableData, 'cells', []).forEach(cell => this.getVisibleData(cell));
			if (!ignoreEmptyRow) { remove(get(tableData, 'cells', []), row => row.length === 0); }
		}
		return tabData;
	}

	public static areRawValuesNotAvailable(componentData: ComponentData): boolean {
		const hasDominantTab = componentData.tabs.some(tab => tab && tab.displayConfig && !tab.displayConfig.hideWithOtherTabs);
		const tabTables = componentData.tabs.filter(tab => (!hasDominantTab || !get(tab, 'displayConfig.hideWithOtherTabs', false)) && !tab.error).map(tab => tab.tableData);
		return !tabTables.some(tableData =>
			tableData.some(table =>
				table.cells.some(cellCollection =>
					cellCollection.some(cell => cell.rawValue)
				)
			)
		);
	}

	/**
	 * Filters the given TabData based on the provided columnIdsToFilter.
	 * The filtering is done by default to headersX and cells.
	 * If shouldFilterHeadersY is true, filtering is done to headersY and cells.
	 *
	 * @param tabData tab to filter
	 * @param columnIdsToFilter column ids to filter out
	 * @param shouldFilterHeadersX false by default; used to filter out either headersX (by default) or headersY
	 */
	public static getFilteredTabData(tabData: TabData, columnIdsToFilter: string[], shouldFilterHeadersY: boolean = false): TabData {
		if (tabData) {
			const indexesOfColumnsToRemove = [];
			const tableData = get(tabData, 'tableData[0]');

			let headersX = get(tableData, 'headersX', []);
			let headersY = get(tableData, 'headersY', []);
			let cells = get(tableData, 'cells', []);

			if (shouldFilterHeadersY) {
				headersY = headersY.filter((headerY, index) => {
					const shouldFilterHeaderY = columnIdsToFilter.includes(headerY.config.id);
					if (shouldFilterHeaderY) {
						indexesOfColumnsToRemove.push(index);
					}

					return !shouldFilterHeaderY;
				});

				cells = get(tableData, 'cells', []).filter((cellArray, index) => !indexesOfColumnsToRemove.includes(index));

			} else {
				headersX = headersX.filter((headerX, index) => {
					const shouldFilterHeaderX = columnIdsToFilter.includes(headerX.config.id);
					if (shouldFilterHeaderX) {
						indexesOfColumnsToRemove.push(index);
					}

					return !shouldFilterHeaderX;
				});

				cells = get(tableData, 'cells', []).map(cellArray => cellArray.filter((cellArray, index) => !indexesOfColumnsToRemove.includes(index)));
			}

			tabData.tableData[0] = new TableData(headersX, headersY, cells, tableData.portfolioData, tableData.firstColumnTitle, tableData.transposed);
		}

		return tabData;
	}

	public static extractMultipleTabsFromTabData(tabData: TabData): TabData[] {
		const tableData = get(tabData, 'tableData[0]');
		return get(tableData, 'cells', []).map((cells, index) => {
			return new TabData(
				new TabConfig(`${tabData.config.id}-${index}`, tabData.config.title, tabData.config.extractorConfig),
				[
					new TableData(
						[tableData.headersX[index]],
						tableData.headersY,
						[cells],
						tableData.portfolioData,
						tableData.firstColumnTitle,
						tableData.transposed
					)
				],
				tabData.displayConfig,
				tabData.error
			);
		});
	}

	public static isDataAvailable(componentData: ComponentData, tabId: string): boolean {
		const isDataValid = componentData && componentData.success;
		const tabData = isDataValid && TabUtil.getVisibleTabData(componentData.getTabById(tabId));
		return TabUtil.isTabValid(tabData);
	}
}
